import { useMemo, useState } from "react";
import { Search, MapPin, Navigation, Store } from "lucide-react";
import InnerHero from "@/components/InnerHero";
import Reveal from "@/components/Reveal";
import { BOLETERIAS, mapLink } from "@/data";

export default function Boleterias() {
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return BOLETERIAS;
        return BOLETERIAS.filter((b) =>
            [b.loc, b.name, b.address].join(" ").toLowerCase().includes(term),
        );
    }, [q]);

    return (
        <div data-testid="boleterias-page">
            <InnerHero
                variant="orange"
                crumb="Boleterías"
                title="Encontrá tu boletería"
                subtitle="Consultá nuestros puntos de venta y despacho. Buscá por localidad y llegá fácil."
            />

            <section className="py-16 lg:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="mx-auto max-w-2xl">
                            <div className="flex items-center gap-3 rounded-2xl border border-black/10 bg-white px-5 py-4 shadow-sm">
                                <Search className="h-5 w-5 text-ink/40" />
                                <input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    data-testid="boleteria-search"
                                    placeholder="Buscar por localidad, nombre o dirección..."
                                    className="w-full bg-transparent text-sm outline-none placeholder:text-ink/40"
                                />
                            </div>
                            <p
                                data-testid="boleteria-count"
                                className="mt-4 text-center text-sm font-semibold text-ink/60"
                            >
                                {filtered.length} boletería
                                {filtered.length === 1 ? "" : "s"} encontrada
                                {filtered.length === 1 ? "" : "s"}
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((b, i) => (
                            <Reveal key={b.loc + b.name} delay={(i % 3) * 0.06}>
                                <div
                                    data-testid={`boleteria-card-${b.loc}`}
                                    className="flex h-full flex-col rounded-3xl border border-black/5 bg-white p-7 shadow-sm transition-shadow hover:shadow-lg"
                                >
                                    <span className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-light px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-brand">
                                        <Store className="h-3.5 w-3.5" />
                                        {b.loc}
                                    </span>
                                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ink">
                                        {b.name}
                                    </h3>
                                    <p className="mt-2 flex items-start gap-2 text-sm text-ink/60">
                                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                        {b.address}
                                    </p>
                                    <a
                                        href={mapLink(b.map)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`boleteria-map-${b.loc}`}
                                        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-black/10 py-3 text-sm font-bold uppercase tracking-wide text-ink transition-colors hover:border-brand hover:bg-brand hover:text-white"
                                    >
                                        <Navigation className="h-4 w-4" />
                                        Cómo llegar
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <p className="mt-12 text-center text-sm text-ink/50">
                            No encontramos boleterías para tu búsqueda.
                        </p>
                    )}
                </div>
            </section>
        </div>
    );
}
